import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useMemo, useState } from 'react';

import type { AccountType, ChartOfAccount } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { useActiveCompany, useActiveCompanyId, useCoa, useUpdateCoa } from '../lib/queries';

export const Route = createFileRoute('/settings/coa')({
  component: CoaEnablementPage,
});

const TYPE_LABELS: Record<AccountType, string> = {
  ASSET: 'สินทรัพย์',
  LIABILITY: 'หนี้สิน',
  EQUITY: 'ส่วนของผู้ถือหุ้น',
  REVENUE: 'รายได้',
  COST: 'ต้นทุน',
  EXPENSE: 'ค่าใช้จ่าย',
};

// Header text for accounts whose grouping columns aren't seeded yet (nullable
// until the COA reseed populates majorGroup/midGroup).
const NO_MAJOR = '(ไม่ระบุกลุ่มหลัก)';
const NO_MID = '(ไม่ระบุกลุ่มย่อย)';

// A flat group of accounts that share the same major + mid group. The grouped
// table renders one section header per major group with a sub-row per mid group.
interface MidGroup {
  mid: string;
  rows: ChartOfAccount[];
}
interface MajorGroup {
  major: string;
  mids: MidGroup[];
}

function CoaEnablementPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  // activeOnly=false: enablement needs every account, including the disabled
  // ones we want to switch back on.
  const { data: rows = [], isPending, error } = useCoa(companyId, false);
  const updateCoa = useUpdateCoa(companyId);

  const [q, setQ] = useState('');

  // Filter on code or Thai name (matches the /coa search behaviour).
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) => r.code.toLowerCase().includes(term) || r.nameTh.toLowerCase().includes(term),
    );
  }, [rows, q]);

  // Group filtered rows by majorGroup → midGroup, each sorted by code. Sections
  // and rows are stable-sorted so the table doesn't jump between renders.
  const groups = useMemo<MajorGroup[]>(() => {
    const byMajor = new Map<string, Map<string, ChartOfAccount[]>>();
    for (const r of filtered) {
      const major = r.majorGroup ?? NO_MAJOR;
      const mid = r.midGroup ?? NO_MID;
      let mids = byMajor.get(major);
      if (!mids) {
        mids = new Map();
        byMajor.set(major, mids);
      }
      const bucket = mids.get(mid);
      if (bucket) bucket.push(r);
      else mids.set(mid, [r]);
    }
    return [...byMajor.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'th'))
      .map(([major, mids]) => ({
        major,
        mids: [...mids.entries()]
          .sort((a, b) => a[0].localeCompare(b[0], 'th'))
          .map(([mid, list]) => ({
            mid,
            rows: [...list].sort((a, b) => a.code.localeCompare(b.code)),
          })),
      }));
  }, [filtered]);

  // KPI roll-up over ALL rows (not just filtered) so the totals stay stable
  // while searching.
  const activeCount = useMemo(() => rows.filter((r) => r.isActive).length, [rows]);
  const inactiveCount = rows.length - activeCount;

  // Bulk enable/disable applies to the visible (filtered) rows only, skipping
  // those already in the target state. Fires one PATCH per changed row.
  function bulkSet(isActive: boolean) {
    const targets = filtered.filter((r) => r.id && r.isActive !== isActive);
    if (targets.length === 0) return;
    const verb = isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
    if (!confirm(`${verb}บัญชีที่แสดงอยู่ ${targets.length} บัญชี?`)) return;
    for (const r of targets) {
      if (!r.id) continue;
      updateCoa.mutate({ id: r.id, data: { isActive } });
    }
  }

  function toggleRow(r: ChartOfAccount, next: boolean) {
    if (!r.id) return;
    updateCoa.mutate({ id: r.id, data: { isActive: next } });
  }

  const busy = updateCoa.isPending;

  return (
    <div className="page">
      <PageHead
        crumbs={['ระบบ', 'ตั้งค่าผังบัญชี']}
        title="ตั้งค่าผังบัญชี (เปิด/ปิดการใช้งาน)"
        actions={
          <>
            <HelpButton guide="settings-coa" />
            <button
              className="btn"
              onClick={() => bulkSet(true)}
              disabled={!companyId || filtered.length === 0 || busy}
            >
              <Icon name="check" size={14} /> เปิดใช้งานที่แสดง
            </button>
            <button
              className="btn"
              onClick={() => bulkSet(false)}
              disabled={!companyId || filtered.length === 0 || busy}
            >
              <Icon name="x" size={14} /> ปิดใช้งานที่แสดง
            </button>
          </>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3,1fr)',
          gap: 10,
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="kpi" style={{ padding: '10px 12px' }}>
          <div className="label">
            <span className="dot" style={{ background: '#1a56a6' }} />
            บัญชีทั้งหมด
          </div>
          <div className="value tnum" style={{ fontSize: 20 }}>
            {rows.length} <span className="unit">บัญชี</span>
          </div>
        </div>
        <div className="kpi" style={{ padding: '10px 12px' }}>
          <div className="label">
            <span className="dot" style={{ background: '#117a3d' }} />
            เปิดใช้งาน
          </div>
          <div className="value tnum" style={{ fontSize: 20 }}>
            {activeCount} <span className="unit">บัญชี</span>
          </div>
        </div>
        <div className="kpi" style={{ padding: '10px 12px' }}>
          <div className="label">
            <span className="dot" style={{ background: '#71717a' }} />
            ปิดใช้งาน
          </div>
          <div className="value tnum" style={{ fontSize: 20 }}>
            {inactiveCount} <span className="unit">บัญชี</span>
          </div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <input
            className="input search w-lg"
            placeholder="ค้นหารหัสบัญชีหรือชื่อบัญชี"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <span className="muted" style={{ fontSize: 13 }}>
            บริษัท: {company?.nameTh ?? company?.code ?? '—'}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button className="btn sm" onClick={() => setQ('')} disabled={!q}>
              รีเซ็ต
            </button>
          </div>
        </div>
        <div className="tbl-wrap" style={{ maxHeight: 560 }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>รหัสบัญชี</th>
                <th>ชื่อบัญชี</th>
                <th style={{ width: 150 }}>ประเภท</th>
                <th style={{ width: 110 }}>การใช้งาน</th>
              </tr>
            </thead>
            <tbody>
              {isPending ? (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    กำลังโหลด…
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td
                    colSpan={4}
                    className="muted"
                    style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                  >
                    โหลดล้มเหลว: {error.message}
                  </td>
                </tr>
              ) : groups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ไม่พบบัญชีที่ตรงกับเงื่อนไข
                  </td>
                </tr>
              ) : (
                groups.map((g) => (
                  <GroupSection key={g.major} group={g} busy={busy} onToggle={toggleRow} />
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="tbl-footer">
          <span>
            แสดง {filtered.length} จาก {rows.length} บัญชี
          </span>
        </div>
      </div>
    </div>
  );
}

interface GroupSectionProps {
  group: MajorGroup;
  busy: boolean;
  onToggle: (r: ChartOfAccount, next: boolean) => void;
}

function GroupSection({ group, busy, onToggle }: GroupSectionProps) {
  // One major-group header, then each mid-group as a lighter sub-header with its
  // accounts beneath. The mid-group sub-header is suppressed when it's the only
  // group and matches the "no grouping" placeholder (keeps ungrouped data flat).
  const flatSingle = group.mids.length === 1 && group.mids[0]!.mid === NO_MID;
  return (
    <>
      <tr className="section-header">
        <td colSpan={4}>{group.major}</td>
      </tr>
      {group.mids.map((m) => (
        <Fragment key={m.mid}>
          {!flatSingle && (
            <tr>
              <td
                colSpan={4}
                className="muted"
                style={{ background: '#fafafa', fontWeight: 500, paddingLeft: 24 }}
              >
                {m.mid}
              </td>
            </tr>
          )}
          {m.rows.map((r) => (
            <tr key={r.code}>
              <td className="code">{r.code}</td>
              <td>{r.nameTh}</td>
              <td className="muted">{TYPE_LABELS[r.type]}</td>
              <td>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    checked={r.isActive}
                    disabled={!r.id || busy}
                    onChange={(e) => onToggle(r, e.target.checked)}
                  />
                  {r.isActive ? (
                    <span className="badge positive">
                      <span className="bdot" />
                      ใช้งาน
                    </span>
                  ) : (
                    <span className="badge neutral">ปิด</span>
                  )}
                </label>
              </td>
            </tr>
          ))}
        </Fragment>
      ))}
    </>
  );
}
